if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();

        this.selectors = {
          viewer: '[id^="GalleryViewer"]',
          thumbnails: '[id^="GalleryThumbnails"]',
          mediaList: '[id^="Slider-Gallery"]',
          mediaItems: ['.product__media-item'],
        };

        this.initialized = false;
        this.sliderInstance = false;
        this.thumbsInstance = false;
        this.deferredLoaded = false;
      }

      connectedCallback() {
        this.bsdObserveLazy();
        FoxTheme.Motion.inView(this, this.init.bind(this));
      }

      // BSD 10-24「轮播只先下第 1 张」：product-thumbnail.liquid 把第 2 张起的真实地址放在 data-bsd-src(set)，
      // 这里在图片进入可视区（上下 600px 内；横向轮播里看不见的那几张被裁掉，不会提前触发）时换上。
      bsdLoadImg(img) {
        if (!img || !img.dataset.bsdSrc) return;
        const src = img.dataset.bsdSrc;
        const srcset = img.dataset.bsdSrcset;
        delete img.dataset.bsdSrc;
        delete img.dataset.bsdSrcset;
        if (this.bsdIO) this.bsdIO.unobserve(img);
        // 记下正在下载的图，用户跳到别的图时可以先撤回，把带宽让给那张
        if (!this.bsdLoading) this.bsdLoading = new Map();
        this.bsdLoading.set(img, { src, srcset });
        const forget = () => this.bsdLoading && this.bsdLoading.delete(img);
        img.addEventListener('load', forget, { once: true });
        img.addEventListener('error', forget, { once: true });
        // 占位图已被 image-lazy 标成 loaded，去掉后它会按原样淡入真图
        img.classList.remove('loaded');
        if (srcset) img.srcset = srcset;
        img.src = src;
      }

      bsdLoadSlide(slide) {
        if (!slide) return;
        slide.querySelectorAll('img[data-bsd-src]').forEach((img) => this.bsdLoadImg(img));
      }

      bsdObserveLazy() {
        const imgs = this.querySelectorAll('img[data-bsd-src]');
        if (!imgs.length) return;
        if (!('IntersectionObserver' in window)) {
          imgs.forEach((img) => this.bsdLoadImg(img));
          return;
        }
        if (!this.bsdIO) {
          this.bsdIO = new IntersectionObserver(
            (entries) => {
              entries.forEach((entry) => {
                if (entry.isIntersecting) this.bsdLoadImg(entry.target);
              });
            },
            { rootMargin: '600px 0px' }
          );
        }
        imgs.forEach((img) => this.bsdIO.observe(img));
      }

      // BSD 10-24b 负责人「这样改了之后，产品主图加载就会很慢」（后续图要滑到才开始下）：
      // 第 1 张照旧最优先；它显示出来后，按顺序一张接一张在后台把后面的轮播图都下好（低优先级，同一时间只下一张，不和第 1 张抢带宽），
      // 用户滑过去时图已经在了。只在轮播模式（手机商品页、快速查看）这样做；电脑商品页是上下排的大图，仍按滚动加载。
      bsdPreloadAll() {
        if (typeof this.sliderInstance !== 'object' || !this.sliderInstance.slider) return;
        if (this.bsdPreloadRunning) return;
        const run = () => {
          if (this.bsdPreloadRunning) return;
          this.bsdPreloadRunning = true;
          this.bsdPreloadStarted = true;
          const next = () => {
            this.bsdInflight = null;
            // 用户正在等某一张时先停一下，等那张出来再接着下
            // 10-25：轮播已滚出屏幕（用户在看下面的卖点卡片等）时也停下，回到轮播再接着下
            if (this.bsdPaused || this.bsdOffscreen) {
              this.bsdPreloadRunning = false;
              return;
            }
            const img = this.querySelector('.product__media-item img[data-bsd-src]');
            if (!img) {
              this.bsdPreloadRunning = false;
              return;
            }
            this.bsdInflight = { img, src: img.dataset.bsdSrc, srcset: img.dataset.bsdSrcset };
            img.fetchPriority = 'low';
            // 原来带 loading="lazy"：横向轮播里看不见的那几张浏览器不会去下，load 事件永远不来，这里改成立即下载
            img.loading = 'eager';
            this.bsdLoadImg(img);
            if (img.complete) {
              setTimeout(next, 0);
            } else {
              // 一张下完再下一张；万一 8 秒还没回来也接着下一张，不卡住
              let moved = false;
              const go = () => {
                if (moved) return;
                moved = true;
                clearTimeout(timer);
                setTimeout(next, 0);
              };
              const timer = setTimeout(go, 8000);
              img.addEventListener('load', go, { once: true });
              img.addEventListener('error', go, { once: true });
            }
          };
          next();
        };
        if (this.bsdPreloadStarted) {
          run();
          return;
        }
        const first = this.querySelector('.product__media-item img:not([data-bsd-src])');
        if (!first || (first.complete && first.naturalWidth > 1)) {
          run();
        } else {
          first.addEventListener('load', run, { once: true });
          first.addEventListener('error', run, { once: true });
          // 保险：第 1 张迟迟不完成时，等整页 load 后也开始
          if (document.readyState === 'complete') setTimeout(run, 3000);
          else window.addEventListener('load', () => setTimeout(run, 1500), { once: true });
        }
      }

      // 当前这张和左右相邻各一张：手指一碰轮播、换页、选颜色跳图时调用，滑过去时下一张已经在下了
      bsdLoadAround(swiper) {
        const slider = swiper || (typeof this.sliderInstance === 'object' && this.sliderInstance.slider);
        if (!slider || !slider.slides) return;
        const i = slider.activeIndex || 0;
        const active = slider.slides[i];
        const neighbours = () => [i - 1, i + 1].forEach((k) => this.bsdLoadSlide(slider.slides[k]));
        const ready = (img) => !img.dataset.bsdSrc && img.complete && img.naturalWidth > 1;
        const pending = active ? [...active.querySelectorAll('.product__media img')].filter((img) => !ready(img)) : [];
        if (!pending.length) {
          neighbours();
          return;
        }
        // 10-24c：用户要看的这张还没下 → 让它独占带宽：后台预加载暂停，正在下的那张先撤回（以后再下），
        // 这张用最高优先级下；它出来后再下左右两张，然后继续后台预加载。
        this.bsdPaused = true;
        if (this.bsdLoading) {
          [...this.bsdLoading.entries()].forEach(([img, info]) => {
            if (active.contains(img) || !img.isConnected || ready(img)) return;
            this.bsdLoading.delete(img);
            img.removeAttribute('srcset');
            img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
            img.dataset.bsdSrc = info.src;
            if (info.srcset) img.dataset.bsdSrcset = info.srcset;
          });
        }
        let done = false;
        const resume = () => {
          if (done) return;
          done = true;
          clearTimeout(timer);
          this.bsdPaused = false;
          this.bsdObserveLazy();
          neighbours();
          if (this.bsdPreloadStarted) this.bsdPreloadAll();
        };
        const timer = setTimeout(resume, 8000);
        pending.forEach((img) => {
          img.addEventListener('load', resume, { once: true });
          img.addEventListener('error', resume, { once: true });
          if (img.dataset.bsdSrc) {
            img.fetchPriority = 'high';
            img.loading = 'eager';
            this.bsdLoadImg(img);
          }
        });
      }

      init() {
        if (this.initialized === true) return;

        this.elements = window.FoxTheme.utils.queryDomNodes(this.selectors, this);
        this.mediaLayout = this.dataset.mediaLayout;
        this.onlyImage = this.dataset.onlyImage === 'true';
        this.enableDesktopSlider = this.dataset.enableDesktopSlider === 'true';
        this.enableMobileThumbnails = this.dataset.enableMobileThumbnails === 'true';
        this.enableImageZoom = this.dataset.enableImageZoom === 'true';
        this.totalMediaCount = parseInt(this.dataset.totalMediaCount) || 0;
        this.initialMediaLimit = parseInt(this.dataset.initialMediaLimit) || this.totalMediaCount;
        this.setSliderOptions();

        const mql = window.matchMedia(FoxTheme.config.mediaQueryMobile);
        mql.onchange = this.updateMediaLayout.bind(this);
        this.updateMediaLayout();

        if (this.enableImageZoom) {
          const allMedia = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
          this.initImageZoom(allMedia);
        }

        this.initialized = true;
        this.scheduleDeferredLoad();
        this.bsdObserveLazy();
        this.bsdPreloadAll();
        this.bsdWatchOffscreen();
      }

      // BSD 10-25 负责人「详情页的图片加载得很慢」（下面卖点卡片的图半天不出）：
      // 后台预加载轮播大图只在轮播在屏幕上（上下 200px 内）时进行；用户往下翻走后，正在下的那张没下完就撤回（以后再下），
      // 带宽让给用户正在看的图；翻回轮播时接着下。首图、滑动、点缩略图都发生在轮播可见时，不受影响。
      bsdWatchOffscreen() {
        if (this.bsdOffIO || !('IntersectionObserver' in window)) return;
        this.bsdOffIO = new IntersectionObserver(
          (entries) => {
            const e = entries[entries.length - 1];
            if (!e) return;
            if (e.isIntersecting) {
              if (!this.bsdOffscreen) return;
              this.bsdOffscreen = false;
              if (this.bsdPreloadStarted && !this.bsdPaused) this.bsdPreloadAll();
              return;
            }
            this.bsdOffscreen = true;
            const f = this.bsdInflight;
            if (!f || !f.img || !f.img.isConnected || f.img.dataset.bsdSrc) return;
            if (f.img.complete && f.img.naturalWidth > 1) return;
            if (this.bsdLoading) this.bsdLoading.delete(f.img);
            f.img.removeAttribute('srcset');
            f.img.src = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
            f.img.dataset.bsdSrc = f.src;
            if (f.srcset) f.img.dataset.bsdSrcset = f.srcset;
          },
          { rootMargin: '200px 0px' }
        );
        this.bsdOffIO.observe(this);
      }

      setSliderOptions() {
        const mediaItemGap = parseInt(this.dataset.mediaItemGap);

        this.sliderOptions = {
          init: false,
          slidesPerView: '1',
          spaceBetween: mediaItemGap,
          loop: false,
          grabCursor: true,
          allowTouchMove: true,
          autoHeight: true,
          navigation: {
            nextEl: this.querySelector('.swiper-button-next'),
            prevEl: this.querySelector('.swiper-button-prev'),
          },
          pagination: {
            el: this.querySelector('.swiper-pagination'),
            clickable: true,
            type: 'fraction',
          },
          threshold: 2,
        };

        this.thumbsOptions = {
          slidesPerView: 4,
          breakpoints: {
            461: {
              slidesPerView: 5,
            },
          },
          spaceBetween: mediaItemGap,
          loop: false,
          freeMode: true,
          watchSlidesProgress: true,
          threshold: 2,
        };

        // BSD 10-23 负责人「这个左右滑动更加顺畅一些」（手机商品页主图下面的缩略图条）：
        // 主题没打包 Swiper 的 FreeMode 模块，freeMode: true 实际不生效，手指一拨只能一格一格吸附、没有惯性；
        // 手机上改用 cssMode（原生横向滚动，iOS 自带惯性、跟手），freeMode 写成 { enabled: true }，
        // 只用来加 swiper-free-mode 类、并让 update() 保持当前滚动位置；吸附在 bsd-pdp-buy.css 里关掉。电脑端不变。
        this.thumbsNative = FoxTheme.config.mqlMobile && this.enableMobileThumbnails;
        if (this.thumbsNative) {
          this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
            cssMode: true,
            freeMode: { enabled: true },
          });
        }

        switch (this.mediaLayout) {
          case 'vertical-carousel':
            this.thumbsOptions = Object.assign({}, this.thumbsOptions, {
              breakpoints: {
                768: {
                  direction: 'vertical',
                  slidesPerView: 'auto',
                  spaceBetween: 0,
                },
              },
            });
            break;
          case 'slider-freemode':
            this.sliderOptions = Object.assign({}, this.sliderOptions, {
              slidesPerView: 'auto',
            });
            break;
        }
      }

      updateMediaLayout() {
        if (FoxTheme.config.mqlMobile) {
          this.initSlider();
        } else {
          if (this.enableDesktopSlider) {
            this.initSlider();
          } else {
            this.destroySlider();
          }
        }
      }

      initSlider() {
        if (typeof this.sliderInstance !== 'object') {
          if ((this.enableDesktopSlider || this.enableMobileThumbnails) && this.elements.thumbnails) {
            this.thumbsInstance = new window.FoxTheme.Carousel(this.elements.thumbnails, this.thumbsOptions);
            this.thumbsInstance.init();

            this.sliderOptions.thumbs = {
              swiper: this.thumbsInstance.slider,
              autoScrollOffset: 2,
            };
          }

          this.sliderInstance = new window.FoxTheme.Carousel(this.elements.viewer, this.sliderOptions, [
            FoxTheme.Swiper.Thumbs,
          ]);
          this.sliderInstance.init();

          this.handleSliderAfterInit();
          this.handleSlideChange();

          this.sliderInstance.slider.init();
          this.syncThumbsPosition();

          const warm = () => this.bsdLoadAround();
          this.elements.viewer.addEventListener('pointerdown', warm, { once: true, passive: true });
          this.elements.viewer.addEventListener('touchstart', warm, { once: true, passive: true });
        }
      }

      destroySlider() {
        if (typeof this.sliderInstance === 'object') {
          this.sliderInstance.slider.destroy();
          this.sliderInstance = false;
        }
      }

      // BSD 10-23：手机缩略图条用原生滚动时，缩略图不要懒加载，滑到才冒出来会显得卡（缩略图是 200 宽的小图，十几张很轻）
      eagerThumbs() {
        if (!this.thumbsNative || !this.elements.thumbnails) return;
        this.elements.thumbnails.querySelectorAll('img[loading="lazy"]').forEach((img) => {
          img.loading = 'eager';
        });
      }

      scheduleDeferredLoad() {
        if (this.deferredLoaded || !this.querySelector('[id^="DeferredSlides"]')) {
          this.deferredLoaded = true;
          this.eagerThumbs();
          return;
        }

        // BSD 10-23：手机上不等空闲再追加，免得用户正在横滑缩略图条时追加、位置跳一下
        if (this.thumbsNative) {
          this.loadDeferredSlides();
          return;
        }

        if ('requestIdleCallback' in window) {
          requestIdleCallback(() => this.loadDeferredSlides(), { timeout: 1500 });
        } else {
          setTimeout(() => this.loadDeferredSlides(), 200);
        }
      }

      ensureDeferredLoaded() {
        if (!this.deferredLoaded) {
          this.loadDeferredSlides();
        }
      }

      loadDeferredSlides() {
        if (this.deferredLoaded) return;

        const deferredSlidesTemplate = this.querySelector('[id^="DeferredSlides"]');
        if (!deferredSlidesTemplate) {
          this.deferredLoaded = true;
          return;
        }

        const deferredSlides = [...deferredSlidesTemplate.content.querySelectorAll('.product__media-item')];
        const deferredThumbsTemplate = this.querySelector('[id^="DeferredThumbnails"]');

        if (typeof this.sliderInstance === 'object' && this.sliderInstance.slider) {
          const swiperWrapper = this.sliderInstance.slider.wrapperEl;
          deferredSlides.forEach((slide) => swiperWrapper.appendChild(slide.cloneNode(true)));

          if (typeof this.thumbsInstance === 'object' && this.thumbsInstance.slider && deferredThumbsTemplate) {
            const thumbsWrapper = this.thumbsInstance.slider.wrapperEl;
            const thumbs = [...deferredThumbsTemplate.content.querySelectorAll('.product__thumbs-item')];
            thumbs.forEach((thumb) => thumbsWrapper.appendChild(thumb.cloneNode(true)));
            this.thumbsInstance.slider.update();
          }

          this.sliderInstance.slider.update();
          this.syncThumbsPosition();
        } else {
          this.elements.mediaList.appendChild(deferredSlidesTemplate.content);
        }

        this.elements.mediaItems = [...this.querySelectorAll('.product__media-item:not(.swiper-slide-duplicate)')];
        this.eagerThumbs();

        if (this.enableImageZoom) {
          this.initImageZoom(this.elements.mediaItems);
        }

        deferredSlidesTemplate.remove();
        deferredThumbsTemplate?.remove();
        this.deferredLoaded = true;
        this.bsdObserveLazy();
        if (this.bsdPreloadStarted) this.bsdPreloadAll();
      }

      syncThumbsPosition() {
        if (typeof this.thumbsInstance !== 'object' || !this.thumbsInstance.slider) return;
        if (typeof this.sliderInstance !== 'object' || !this.sliderInstance.slider) return;

        const thumbsSlider = this.thumbsInstance.slider;
        const mainSlider = this.sliderInstance.slider;
        if (thumbsSlider.destroyed || mainSlider.destroyed) return;

        const realIndex = mainSlider.realIndex;

        // Manually update active class since slideToLoop uses runCallbacks: false,
        // which prevents the Thumbs module from updating the active state.
        thumbsSlider.slides?.forEach((slide, i) => {
          slide.classList.toggle('swiper-slide-thumb-active', i === realIndex);
        });

        // BSD 10-23：手机原生滚动时，当前那张已经看得见就不改滚动位置（改 scrollLeft 会打断正在进行的惯性滑动）
        if (thumbsSlider.params.cssMode) {
          const active = thumbsSlider.slides && thumbsSlider.slides[realIndex];
          if (active) {
            const box = thumbsSlider.el.getBoundingClientRect();
            const rect = active.getBoundingClientRect();
            if (rect.left >= box.left - 1 && rect.right <= box.right + 1) return;
          }
        }

        thumbsSlider.slideTo(realIndex, 0);
      }

      destroyLightbox() {
        if (this.lightbox) {
          this.lightbox.destroy();
          this.lightbox = null;
        }
      }

      initThumbsSlider() {
        if (typeof this.thumbsInstance !== 'object') {
          this.thumbsInstance = new window.FoxTheme.Carousel(this.selectors.thumbnails, this.thumbsOptions);
          this.thumbsInstance.init();
        }
      }

      initImageZoom(items = []) {
        this.destroyLightbox();
        let dataSource = [];
        if (items.length > 0) {
          items.forEach((media) => {
            switch (media.dataset.mediaType) {
              case 'model':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('product-model').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'video':
              case 'external_video':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  html: `<div class="pswp__item--${media.dataset.mediaType}">${
                    media.querySelector('video-element').outerHTML
                  }</div>`,
                  mediaType: media.dataset.mediaType,
                });
                break;
              case 'image':
                dataSource.push({
                  id: media.dataset.mediaIndex,
                  src: media.dataset.src,
                  width: media.dataset.pswpWidth,
                  height: media.dataset.pswpHeight,
                  alt: media.dataset.alt || '',
                  mediaType: media.dataset.mediaType,
                });
                break;
            }
          });
        }

        this.lightbox = new window.FoxTheme.PhotoSwipeLightbox({
          dataSource: dataSource,
          pswpModule: window.FoxTheme.PhotoSwipe,
          bgOpacity: 1,
          arrowPrev: false,
          arrowNext: false,
          zoom: false,
          close: false,
          counter: false,
          preloader: false,
          preload: [1, 4],
        });

        this.lightbox.addFilter('thumbEl', (thumbEl, { id }, index) => {
          if (this.sliderInstance && this.sliderInstance.slider) {
            const { slides, activeIndex } = this.sliderInstance.slider;
            if (slides[activeIndex]) {
              const el = slides[activeIndex].querySelector('img');
              if (el) {
                return el;
              }
            }
          }

          return thumbEl;
        });

        this.lightbox.addFilter('placeholderSrc', (placeholderSrc, { data }) => {
          if (data.src) {
            return data.src;
          }
          return placeholderSrc;
        });

        this.lightbox.on('change', () => {
          window.pauseAllMedia(this);
        });

        this.lightbox.on('destroy', () => {
          const { currIndex } = this.lightbox.pswp;

          if (this.sliderInstance && this.sliderInstance.slider) {
            this.sliderInstance.slider.slideToLoop(currIndex, 0, false);
            this.bsdLoadAround();
          }
        });

        this.lightbox.on('pointerDown', (e) => {
          if (this.lightbox.pswp.currSlide.data.mediaType != 'image') {
            e.preventDefault();
          }
        });

        this.lightbox.on('uiRegister', () => {
          if (!this.onlyImage) {
            this.lightbox.pswp.ui.registerElement({
              name: 'next',
              ariaLabel: 'Next slide',
              order: 3,
              isButton: true,
              html: '<svg class="pswp-icon-next flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.next();
              },
            });
            this.lightbox.pswp.ui.registerElement({
              name: 'prev',
              ariaLabel: 'Previous slide',
              order: 1,
              isButton: true,
              html: '<svg class="pswp-icon-prev rtl-flip-x" viewBox="0 0 100 100"><path d="M 10,50 L 60,100 L 65,90 L 25,50  L 65,10 L 60,0 Z"></path></svg>',
              onClick: (event, el) => {
                this.lightbox.pswp.prev();
              },
            });
          }
          this.lightbox.pswp.ui.registerElement({
            name: 'close-zoom',
            ariaLabel: 'Close zoom image',
            order: 2,
            isButton: true,
            html: '<svg xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false" role="presentation" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><line x1="18" y1="6" x2="6" y2="18"></line><line x1="6" y1="6" x2="18" y2="18"></line></svg>',
            onClick: (event, el) => {
              this.lightbox.pswp.close();
            },
          });
        });

        this.lightbox.init();

        FoxTheme.utils.addEventDelegate({
          selector: '.js-photoswipe--zoom',
          context: this,
          handler: (e, media) => {
            if (media.dataset.mediaType === 'image') {
              const index = Number(media.dataset.mediaIndex) || 0;
              this.lightbox.loadAndOpen(index);
            }
          },
        });
      }

      handleSliderAfterInit() {
        this.sliderInstance.slider.on('afterInit', (swiper) => {
          const { slides, activeIndex } = swiper;

          if (slides[activeIndex]) {
            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      handleSlideChange() {
        this.sliderInstance.slider.on('realIndexChange', (swiper) => {
          const { slides, activeIndex, realIndex, thumbs } = swiper;
          this.bsdLoadAround(swiper);

          if (thumbs.swiper) {
            thumbs.swiper.slideTo(realIndex);
          }

          if (slides[activeIndex]) {
            this.playActiveMedia(slides[activeIndex]);

            const isModelMediaType = slides[activeIndex].dataset.mediaType === 'model';
            this.toggleSliderDraggableState(!isModelMediaType);
          }
        });
      }

      toggleSliderDraggableState(isDraggable) {
        if (this.sliderInstance.slider.allowTouchMove !== isDraggable) {
          this.sliderInstance.slider.allowTouchMove = isDraggable;
        }
      }

      playActiveMedia(selected) {
        const deferredMedia = selected.querySelector('product-model');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      setActiveMedia(variant) {
        if (!variant || !variant.hasOwnProperty('featured_media') || !variant.featured_media) return;

        const targetPosition = variant.featured_media.position || 0;
        if (targetPosition > this.initialMediaLimit) {
          this.ensureDeferredLoaded();
        }

        if (typeof this.sliderInstance === 'object' && this.sliderInstance.slider) {
          this.sliderInstance.slider.slideToLoop(targetPosition - 1, 0, false);
          this.syncThumbsPosition();
          this.bsdLoadAround();
        } else {
          this.sortMediaItems(variant);
        }
      }

      sortMediaItems(variant) {
        this.ensureDeferredLoaded();
        let newMedias = this.elements.mediaItems;

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        newMedias.some((media, index) => {
          if (media.dataset.mediaId == variant.featured_media.id) {
            const [element] = newMedias.splice(index, 1);
            newMedias.unshift(element);
            return true;
          }
        });

        this.elements.mediaList.innerHTML = '';
        newMedias.forEach((media) => {
          this.elements.mediaList.appendChild(media);
        });

        // Reset ordering.
        newMedias.sort(function (a, b) {
          return a.dataset.mediaIndex - b.dataset.mediaIndex;
        });

        if (this.enableImageZoom) {
          this.initImageZoom(newMedias);
        }

        this.bsdLoadSlide(this.querySelector(`[data-media-id="${variant.featured_media.id}"]`));
        this.bsdObserveLazy();

        if (!FoxTheme.config.mqlMobile) {
          const selectedMedia = this.querySelector(`[data-media-id="${variant.featured_media.id}"]`);
          if (selectedMedia) {
            window.scrollTo({ top: selectedMedia.offsetTop, behavior: FoxTheme.config.disableAnimations ? 'auto' : 'smooth' });
          }
        }
      }
    }
  );
}
